<template>
  <div class="currency-input-wrapper">
    <input
      :id="`${id}`"
      v-bind="listeners"
      v-currency-input="{
        precision,
        prefix: props.prefix,
        disableNegative: props.disableNegative,
        min: props.min,
        max: props.max,
        roundToMaxValue: false,
        minimumNumberOfCharacters: props.minimumNumberOfCharacters,
      }"
      type="tel"
      class="currency-input"
      :class="{ 'shake-animation': isShaking }"
      :value="formattedValue"
      :disabled="props.disabled"
      @change="change"
    >
    <span class="text-xs text-red-500">{{ errorText }}</span>
    <Transition name="fade">
      <div
        v-if="showMaxOverflowMessage"
        class="max-overflow-message"
      >
        {{ maxOverflowMessage }}
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
/**
 * CurrencyInput.vue
 *
 * TODO - Add this to component library
 *
 * This is a configurable component that will input of an amount of currency
 * Props for min and max values, Prefix for Currency symbols and input sanitization
 *
 * Pass a v-model of a string or int and it will update the values for you
 *
 * Based on the v-money-3 library with substantial modifications for our needs
 * MIT License
 *
 * Copyright (c) 2021 Jonathan Martins <web@jonathanmartins.com.br>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import {
  computed,
  getCurrentInstance,
  ref,
  toRefs,
  useAttrs,
  watch,
} from 'vue';
import defaults from './options';
import format from './format';
import unformat from './unformat';

import {
  filterOptRestrictions,
  validateRestrictedInput,
} from './Utils';

const props = defineProps({
  id: {
    required: false,
    type: [Number, String],
    default: () => {
      const instance = getCurrentInstance();
      if (instance) {
        return instance.uid;
      }
      return null;
    },
  },
  modelValue: {
    required: false, // Not needed if using v-on directive
    type: [Number, String],
  },
  precision: {
    type: Number,
    default: () => defaults.precision,
  },
  prefix: {
    type: String,
    default: () => defaults.prefix,
    validator(value: string) {
      return validateRestrictedInput(value);
    },
  },
  disableNegative: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  max: {
    type: [Number, String],
    default: () => defaults.max,
  },
  min: {
    type: [Number, String],
    default: () => defaults.min,
  },
  minimumNumberOfCharacters: {
    type: Number,
    default: () => defaults.minimumNumberOfCharacters,
  },
  roundToMaxValue: {
    type: Boolean,
    default: true
  },
  roundToMinValue: {
    type: Boolean,
    default: true
  },
  maxOverflowMessage: {
    type: String,
    default: '',
  },
});

const {modelValue, precision} = toRefs(props);

let value: string | number = modelValue.value;

const formattedValue = ref(format(value, props));
const errorText = ref("");
const isShaking = ref(false);
const showMaxOverflowMessage = ref(false);

watch(modelValue, modelValueWatcher);

function modelValueWatcher(value: string | number | null | undefined): void {
  const formatted = format(
    value,
    filterOptRestrictions({...props}),
  );
  if (formatted !== formattedValue.value) {
    formattedValue.value = formatted;
  }
}

let lastValue: string | number | null = null;
const emit =
  defineEmits<{ (e: 'update:model-value', value: string | number): void }>();

function triggerMaxOverflowFeedback() {
  if (!props.maxOverflowMessage) return;

  // Trigger shake animation
  isShaking.value = true;
  setTimeout(() => {
    isShaking.value = false;
  }, 500);

  // Show overflow message
  showMaxOverflowMessage.value = true;
  setTimeout(() => {
    showMaxOverflowMessage.value = false;
  }, 5000);
}

function change(evt: Event) {
  let rawValue: string | number = (evt.target as HTMLInputElement).value;

  // Check if the attempted value exceeds or equals the max, and there is an overflow message
  if (props.max && props.maxOverflowMessage) {
    // Check raw unformatted value before processing to detect overflow attempts
    let unformattedValue = unformat(
      rawValue,
      filterOptRestrictions({...props}),
    );
    if (typeof unformattedValue === 'string') {
      unformattedValue = Number(unformattedValue);
    }
    const max = Number(parseFloat(props.max.toString()).toFixed(2));

    if (unformattedValue >= max) {
      // Trigger the animation to show the attempted value exceeds the maximum
      triggerMaxOverflowFeedback();
    }
  }

  // Now process the value normally (this will clamp to max if needed)
  let value: string | number = unformat(
    rawValue,
    filterOptRestrictions({...props}),
  );

  if (value !== lastValue) {
    lastValue = value;
    emit('update:model-value', value);
  }
}

const attrs = useAttrs();
const listeners = computed(() => {
  const payload = {
    ...attrs,
  };

  delete payload['onUpdate:modelValue'];
  return payload;
});
</script>

<script lang="ts">
import currencyInput from './directive';

export default {
  name: 'CurrencyInput',
  directives: {
    currencyInput,
  },
  inheritAttrs: false,
};
</script>

<style lang="scss">
.fpc-form-field {
  @apply focus:bg-fpc_slate text-default_black border-none disabled:bg-default_white disabled:bg-opacity-90
}

.currency-input-wrapper {
  position: relative;
}

.shake-animation {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-4px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(4px);
  }
}

.max-overflow-message {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fee2e2;
  color: #dc2626;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  z-index: 10;
  border: 1px solid #fecaca;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
